import Table from "components/Table";
import ConfirmationModal from "pages/data-table-view/components/ConfirmationModal";
import Pagination from "pages/data-table-view/components/Pagination";
import SearchInput from "pages/data-table-view/components/SearchInput";
import Toast from "pages/data-table-view/components/Toast";
import { useEffect, useRef, useState } from "react";
import useTableSort from "hooks/useTableSort";
import { API_ENDPOINTS } from "config/config";
import { CircleCheck, CircleX, Pencil, Trash2 } from "lucide-react";
import useHttp from "hooks/useHttp";
import useTableFilters from "hooks/useTableFilters";
import { Select, DatePicker } from "antd";
import dayjs from "dayjs";
import TableFilterPanel from "../../../components/ui/TableFilterPanel";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { useSettings } from "context/GlobalSettingContext";
import ExpenseSummaryCards from "./ExpenseSummaryCards";
import ExpenseCategoryTabs from "./ExpenseCategoryTabs";
import { buildTableDateFilterParams } from "utils/dateRangeParams";
import {
  EXPENSE_CATEGORY_OPTIONS,
  EXPENSE_SUBCATEGORIES,
  EXPENSE_TYPE,
  EXPENSE_TYPE_OPTIONS,
  formatExpenseLabel,
  getSubcategoryLabel,
} from "constants/expenseConstants";
import { formatExpenseRecordDate, getExpenseTypeLabel } from "utils/expenseUtils";

const SESSION_KEY = "expense_filters";

const DEFAULT_FILTERS = {
  dateRangeType: "thisMonth",
  customDateRange: null,
  category: undefined,
  subcategory: undefined,
};

const SOURCE_STYLES = {
  manual: "bg-blue-100 text-blue-800",
  auto_commission: "bg-purple-100 text-purple-800",
};

const formatLabel = formatExpenseLabel;

const formatCurrency = (value) =>
  `$${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const SourceBadge = ({ source }) => (
  <span
    className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
      SOURCE_STYLES[source] || "bg-gray-100 text-gray-800"
    }`}
  >
    {formatLabel(source)}
  </span>
);

const isReadOnlyRow = (item) => item.source === "auto_commission" || item.isDeleted;

export default function ExpenseTable() {
  const { settings } = useSettings();
  const api = useHttp();
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const { filters, setFilters, hydrated } = useTableFilters(SESSION_KEY, DEFAULT_FILTERS);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    subcategory: "",
    amount: "",
    quantity: "",
    unitPrice: "",
    expenseDate: dayjs(),
    expenseType: EXPENSE_TYPE.VARIABLE,
  });
  const [editingItem, setEditingItem] = useState(null);

  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [summaryRefreshKey, setSummaryRefreshKey] = useState(0);
  const didMaterialize = useRef(false);

  const { sortConfig, requestSort } = useTableSort({
    initialKey: "createdAt",
    initialDirection: "descending",
    dateKeyMap: {
      createdInfo: "createdAt",
      updatedInfo: "updatedAt",
      deletedInfo: "deletedAt",
    },
  });

  const fetchExpenses = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      // Idempotent, atomic: materialize the current month's recurring occurrences
      // once per mount before the first read (GET endpoints are read-only).
      if (!didMaterialize.current) {
        didMaterialize.current = true;
        try {
          await api.post(
            API_ENDPOINTS.MATERIALIZE_RECURRING_EXPENSE,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch {
          // Non-blocking: cron still materializes; never block the list on this.
        }
      }
      const response = await api.get(API_ENDPOINTS.GET_EXPENSE, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: currentPage,
          limit: itemsPerPage,
          search: searchQuery,
          ...buildTableDateFilterParams(filters),
          category: filters.category,
          subcategory: filters.subcategory,
        },
      });

      const { records, count } = response.data.data;
      setData(records);
      setTotalRecords(count);
      setError(null);
    } catch {
      setError("Failed to fetch expenses.");
      setToast({ show: true, message: "Failed to fetch expenses.", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!hydrated) return;
    fetchExpenses();
  }, [currentPage, itemsPerPage, searchQuery, filters, hydrated]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleSearchClear = () => {
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handleCreateClick = () => {
    setEditingItem(null);
    setFormData({
      name: "",
      category: filters.category || "",
      subcategory: "",
      amount: "",
      quantity: "",
      unitPrice: "",
      expenseDate: dayjs(),
      expenseType: EXPENSE_TYPE.VARIABLE,
    });
    setIsFormOpen(true);
  };

  const handleEditClick = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || "",
      category: item.category || "",
      subcategory: item.subcategory || "",
      amount: item.amount ?? "",
      quantity: item.quantity ?? "",
      unitPrice: item.unitPrice ?? "",
      expenseDate: item.expenseDate ? dayjs(item.expenseDate) : item.createdAt ? dayjs(item.createdAt) : dayjs(),
      expenseType: item.expenseType || EXPENSE_TYPE.VARIABLE,
    });
    setIsFormOpen(true);
  };

  const handleFormSubmit = async () => {
    const token = localStorage.getItem("authToken");
    try {
      const expenseType =
        formData.expenseType === EXPENSE_TYPE.FIXED
          ? EXPENSE_TYPE.FIXED
          : EXPENSE_TYPE.VARIABLE;
      const payload = {
        name: formData.name.trim(),
        category: formData.category,
        subcategory: formData.subcategory || undefined,
        amount: Number(formData.amount),
        quantity: formData.quantity !== "" ? Number(formData.quantity) : undefined,
        unitPrice: formData.unitPrice !== "" ? Number(formData.unitPrice) : undefined,
        expenseDate: formData.expenseDate
          ? formData.expenseDate.startOf("day").toISOString()
          : undefined,
        expenseType,
      };
      if (editingItem) {
        await api.put(`${API_ENDPOINTS.UPDATE_EXPENSE}/${editingItem._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setToast({ show: true, message: "Expense updated", type: "success" });
      } else {
        await api.post(API_ENDPOINTS.CREATE_EXPENSE, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setToast({ show: true, message: "Expense created", type: "success" });
      }
      setIsFormOpen(false);
      fetchExpenses();
      setSummaryRefreshKey((k) => k + 1);
    } catch (err) {
      const message =
        err?.response?.data?.message || "Failed to save expense";
      setToast({ show: true, message, type: "error" });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    const token = localStorage.getItem("authToken");

    try {
      await api.delete(`${API_ENDPOINTS.DELETE_EXPENSE}/${itemToDelete._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setToast({ show: true, message: "Expense deleted", type: "success" });
      fetchExpenses();
      setSummaryRefreshKey((k) => k + 1);
    } catch {
      setToast({ show: true, message: "Failed to delete expense", type: "error" });
    } finally {
      setItemToDelete(null);
      setShowDeleteModal(false);
    }
  };

  const downloadExcel = async (data, columns, settings = {}) => {
    if (!data || data.length === 0) return;

    const formatDateTime = (dateString) => {
      if (!dateString) return "-";
      const d = new Date(dateString);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");
      const seconds = String(d.getSeconds()).padStart(2, "0");
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    };

    const hasDeletedData = data.some((item) => item.deletedAt);

    const exportColumns = columns.filter(
      (col) =>
        col.key !== "actions" &&
        (col.key !== "deletedInfo" ||
          (settings?.isShowDeletedRecords && hasDeletedData))
    );

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Expense Report");

    worksheet.columns = exportColumns.map((col) => ({
      header: col.label,
      key: col.key,
      width: 20,
      style: {
        alignment: {
          horizontal:
            ["amount", "quantity", "unitPrice"].includes(col.key)
              ? "right"
              : "left",
        },
      },
    }));

    data.forEach((item, idx) => {
      const rowData = {};
      exportColumns.forEach((col) => {
        let cellValue = "-";

        if (col.key === "number") {
          cellValue = idx + 1;
        } else if (["createdInfo", "updatedInfo", "deletedInfo"].includes(col.key)) {
          const dateKey =
            col.key === "createdInfo"
              ? "createdAt"
              : col.key === "updatedInfo"
                ? "updatedAt"
                : "deletedAt";
          const byKey =
            col.key === "createdInfo"
              ? "createdBy"
              : col.key === "updatedInfo"
                ? "updatedBy"
                : "deletedBy";
          if (item[dateKey]) {
            const dateValue = formatDateTime(item[dateKey]);
            const byValue = item[byKey] || "-";
            cellValue = `${dateValue} by ${byValue}`;
          }
        } else if (["amount", "unitPrice"].includes(col.key)) {
          cellValue =
            item[col.key] !== undefined &&
            item[col.key] !== null &&
            !isNaN(item[col.key]) &&
            Number(item[col.key]) > 0
              ? Number(item[col.key])
              : null;
        } else if (col.key === "quantity") {
          cellValue =
            item.quantity !== undefined && item.quantity !== null
              ? Number(item.quantity)
              : "-";
        } else if (col.key === "category" || col.key === "source") {
          cellValue = formatLabel(item[col.key]);
        } else if (col.key === "expenseType") {
          cellValue = getExpenseTypeLabel(item.expenseType || EXPENSE_TYPE.VARIABLE);
        } else if (col.key === "isDeleted") {
          cellValue = item.isDeleted ? "Yes" : "No";
        } else {
          cellValue =
            item[col.key] !== null &&
            item[col.key] !== undefined &&
            item[col.key] !== ""
              ? item[col.key]
              : "-";
        }

        rowData[col.key] = cellValue;
      });

      worksheet.addRow(rowData);
    });

    exportColumns.forEach((col, index) => {
      if (["amount", "unitPrice"].includes(col.key)) {
        worksheet.getColumn(index + 1).numFmt = '"$"#,##0.00';
      }
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `Expense_Report_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  const handleSort = (key) => {
    requestSort(key, data, setData, columns);
  };

  const baseColumns = [
    {
      key: "number",
      label: "No.",
      render: (item, idx) => {
        const page = Number(currentPage) || 1;
        const perPage = Number(itemsPerPage) || 10;
        let rowIdx =
          typeof idx === "number" ? idx : data.findIndex((d) => d._id === item._id);
        return (page - 1) * perPage + rowIdx + 1;
      },
    },
    {
      key: "name",
      label: "Name",
      sortable: true,
      render: (item) => item.name || "-",
    },
    {
      key: "category",
      label: "Category",
      sortable: true,
      render: (item) => formatLabel(item.category),
    },
    {
      key: "subcategory",
      label: "Subcategory",
      sortable: true,
      render: (item) => getSubcategoryLabel(item.category, item.subcategory),
    },
    {
      key: "source",
      label: "Source",
      sortable: true,
      render: (item) => <SourceBadge source={item.source} />,
    },
    {
      key: "expenseType",
      label: "Expense Type",
      sortable: true,
      render: (item) =>
        getExpenseTypeLabel(item.expenseType || EXPENSE_TYPE.VARIABLE),
    },
    {
      key: "quantity",
      label: "Quantity",
      sortable: true,
      sortValue: (item) => Number(item.quantity) || 0,
      render: (item) =>
        item.quantity !== undefined && item.quantity !== null ? item.quantity : "-",
      className: "text-right",
      headerClassName: "text-right",
      heaserTextAlign: "justify-end",
    },
    {
      key: "unitPrice",
      label: "Unit Price",
      sortable: true,
      sortValue: (item) => Number(item.unitPrice) || 0,
      render: (item) =>
        item.unitPrice !== undefined && item.unitPrice !== null
          ? formatCurrency(item.unitPrice)
          : "-",
      className: "text-right",
      headerClassName: "text-right",
      heaserTextAlign: "justify-end",
    },
    {
      key: "amount",
      label: "Amount",
      sortable: true,
      sortValue: (item) => Number(item.amount) || 0,
      render: (item) => formatCurrency(item.amount),
      className: "text-right",
      headerClassName: "text-right",
      heaserTextAlign: "justify-end",
    },
    {
      key: "expenseDate",
      label: "Expense Date",
      sortable: true,
      sortValue: (item) => {
        const raw = item.expenseDate ?? item.createdAt;
        return raw ? new Date(raw).getTime() : 0;
      },
      render: (item) => formatExpenseRecordDate(item, settings?.expenseRecordBy || "expenseDate"),
    },
    {
      key: "createdInfo",
      label: "Created Info",
      sortable: true,
      render: (item) => {
        const date = item.createdAt
          ? new Date(item.createdAt).toLocaleString("en-US")
          : "-";
        return (
          <>
            <div>{date}</div>
            <div style={{ fontSize: "12px", color: "#666" }}>
              by {item.createdBy || "-"}
            </div>
          </>
        );
      },
    },
    {
      key: "updatedInfo",
      label: "Updated Info",
      sortable: true,
      render: (item) => {
        const date = item.updatedAt
          ? new Date(item.updatedAt).toLocaleString("en-US")
          : "-";
        return (
          <>
            <div>{date}</div>
            <div style={{ fontSize: "12px", color: "#666" }}>
              by {item.updatedBy || "-"}
            </div>
          </>
        );
      },
    },
    {
      key: "actions",
      label: "Actions",
      render: (item) => {
        const readOnly = isReadOnlyRow(item);
        return (
          <div className="flex gap-2">
            <button
              onClick={() => !readOnly && handleEditClick(item)}
              disabled={readOnly}
              className={readOnly ? "opacity-50 cursor-not-allowed" : ""}
            >
              <Pencil className="w-4 h-4 text-blue-600" />
            </button>
            <button
              onClick={() => {
                if (!readOnly) {
                  setItemToDelete(item);
                  setShowDeleteModal(true);
                }
              }}
              disabled={readOnly}
              className={readOnly ? "opacity-50 cursor-not-allowed" : ""}
            >
              <Trash2 className="w-4 h-4 text-red-600" />
            </button>
          </div>
        );
      },
    },
  ];

  const isDeletedExists = settings?.isShowDeletedRecords && data.some((item) => item.isDeleted);

  // Insert isDeleted / deletedInfo before actions (last column).
  const columns = isDeletedExists
    ? [
        ...baseColumns.slice(0, -3),
        {
          key: "isDeleted",
          label: "is Deleted",
          sortable: true,
          render: (item) =>
            item.isDeleted ? (
              <CircleCheck className="text-green-500" />
            ) : (
              <CircleX className="text-red-500" />
            ),
        },
        ...baseColumns.slice(-3, -1),
        {
          key: "deletedInfo",
          label: "Deleted Info",
          sortable: true,
          render: (item) => {
            const date = item.deletedAt
              ? new Date(item.deletedAt).toLocaleString("en-US")
              : "-";
            return !item.deletedAt ? (
              <>-</>
            ) : (
              <>
                <div>{date}</div>
                <div style={{ fontSize: "12px", color: "#666" }}>
                  by {item.deletedBy || "-"}
                </div>
              </>
            );
          },
        },
        ...baseColumns.slice(-1),
      ]
    : baseColumns;

  const isSaveButtonEnabled =
    formData.name.trim() !== "" &&
    formData.category !== "" &&
    formData.amount !== "" &&
    formData.expenseDate &&
    !isNaN(Number(formData.amount)) &&
    Number(formData.amount) > 0;

  const totalPages = Math.ceil(totalRecords / itemsPerPage);
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const indexOfLastItem = Math.min(indexOfFirstItem + itemsPerPage, totalRecords);

  const SearchInputComponent = () => (
    <SearchInput
      value={searchQuery}
      onChange={handleSearchChange}
      onClear={handleSearchClear}
      placeholder="Search Expenses"
    />
  );

  const subcategoryOptions = filters.category
    ? EXPENSE_SUBCATEGORIES[filters.category] || []
    : [];

  const formSubcategoryOptions = formData.category
    ? EXPENSE_SUBCATEGORIES[formData.category] || []
    : [];

  const FilterComponent = () => (
    <div className="flex items-center gap-3 flex-shrink-0 flex-wrap">
      <TableFilterPanel
        filters={filters}
        onFiltersChange={(newFilters) => {
          setFilters({ ...filters, ...newFilters });
          setCurrentPage(1);
        }}
      />
      {filters.category && subcategoryOptions.length > 0 && (
        <Select
          allowClear
          placeholder="Subcategory"
          value={filters.subcategory}
          onChange={(val) => {
            setFilters({ ...filters, subcategory: val });
            setCurrentPage(1);
          }}
          style={{ width: 180 }}
          options={subcategoryOptions}
        />
      )}
    </div>
  );

  const AddExpenseButton = ({ className = "" }) => (
    <button
      onClick={handleCreateClick}
      className={`bg-primary-600 text-white px-3 py-1.5 rounded hover:bg-primary-700 text-sm flex-shrink-0 h-8 ${className}`}
    >
      Add Expense
    </button>
  );

  const DownloadCSVButton = ({ className = "" }) => (
    <button
      onClick={() => downloadExcel(data, columns, settings)}
      disabled={data.length === 0}
      className={`bg-green-600 text-white px-3 py-1.5 rounded text-sm flex-shrink-0 h-8 ${
        data.length === 0
          ? "opacity-50 cursor-not-allowed"
          : "hover:bg-green-700"
      } ${className}`}
    >
      Download CSV
    </button>
  );

  const ShowDropdown = ({ className = "" }) => (
    <div className={`flex items-center gap-2 flex-shrink-0 ${className}`}>
      <span className="text-sm text-gray-500">Show:</span>
      <Select
        className="!w-[70px]"
        onChange={(val) => {
          setItemsPerPage(val);
          setCurrentPage(1);
        }}
        value={itemsPerPage}
        options={[10, 25, 50, 100, 500, 1000].map((n) => ({ label: n, value: n }))}
      />
    </div>
  );

  return (
    <div className="bg-background">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <ExpenseCategoryTabs
          activeCategory={filters.category}
          onChange={(category) => {
            setFilters({ ...filters, category, subcategory: undefined });
            setCurrentPage(1);
          }}
        />
        <ExpenseSummaryCards
          api={api}
          filters={filters}
          search={searchQuery}
          refreshKey={summaryRefreshKey}
        />

        <div className="p-4 border-b border-gray-200">
          <div className="hidden [@media(min-width:1353px)]:flex [@media(min-width:1353px)]:items-center [@media(min-width:1353px)]:justify-between gap-4">
            <div className="flex-1">
              <SearchInputComponent />
            </div>
            <FilterComponent />
            <AddExpenseButton className="px-4" />
            <DownloadCSVButton className="px-4" />
            <ShowDropdown />
          </div>

          <div className="hidden lg:flex [@media(min-width:1353px)]:hidden lg:items-center lg:justify-between gap-3">
            <div className="flex-1">
              <SearchInputComponent />
            </div>
            <div className="flex-shrink-0">
              <FilterComponent />
            </div>
            <AddExpenseButton />
            <DownloadCSVButton />
            <ShowDropdown />
          </div>

          <div className="hidden md:block lg:hidden">
            <div className="flex items-center justify-between gap-4 mb-4">
              <SearchInputComponent />
              <FilterComponent />
            </div>

            <div className="flex items-center justify-between gap-4">
              <div className="flex gap-3">
                <AddExpenseButton className="px-4" />
                <DownloadCSVButton className="px-4" />
              </div>
              <ShowDropdown />
            </div>
          </div>

          <div className="block md:hidden space-y-4">
            <div>
              <SearchInputComponent />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-4">
              <div className="flex-shrink-0">
                <FilterComponent />
              </div>
              <ShowDropdown />
            </div>

            <div className="flex flex-wrap gap-2 sm:gap-3">
              <AddExpenseButton />
              <DownloadCSVButton />
            </div>
          </div>
        </div>

        <Table
          columns={columns}
          data={data}
          isLoading={isLoading}
          error={error}
          sortConfig={sortConfig}
          onSort={handleSort}
          onRetry={fetchExpenses}
          emptyMessage="No Expense found. Try adjusting your search."
          getRowClassName={(item) =>
            item.isDeleted ? "bg-red-50 text-gray-500 hover:bg-red-100 focus-within:bg-red-100" : ""
          }
        />

        {!isLoading && !error && data.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={totalRecords}
            itemsPerPage={itemsPerPage}
            showingFrom={indexOfFirstItem + 1}
            showingTo={indexOfLastItem}
          />
        )}
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Delete Expense"
        message="Are you sure you want to delete this expense?"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteModal(false)}
        danger
      />

      {isFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-md w-[450px]">
            <h2 className="text-lg font-semibold mb-4">
              {editingItem ? "Edit Expense" : "Add Expense"}
            </h2>

            <div className="flex flex-col gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Name"
                  className="border border-gray-300 rounded px-3 py-2 w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.category}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      category: e.target.value,
                      subcategory: "",
                    }))
                  }
                  className="border border-gray-300 rounded px-3 py-2 w-full"
                >
                  <option value="">Select Category</option>
                  {EXPENSE_CATEGORY_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>

              {formSubcategoryOptions.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subcategory
                  </label>
                  <select
                    value={formData.subcategory}
                    onChange={(e) => {
                      const subcategory = e.target.value;
                      const label =
                        formSubcategoryOptions.find((s) => s.value === subcategory)?.label || "";
                      setFormData((prev) => ({
                        ...prev,
                        subcategory,
                        name: label || prev.name,
                      }));
                    }}
                    className="border border-gray-300 rounded px-3 py-2 w-full"
                  >
                    <option value="">Select Subcategory</option>
                    {formSubcategoryOptions.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expense Type
                </label>
                <div className="flex rounded border border-gray-300 overflow-hidden">
                  {EXPENSE_TYPE_OPTIONS.map((opt) => {
                    const selected = formData.expenseType === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            expenseType: opt.value,
                          }))
                        }
                        className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                          selected
                            ? "bg-primary-600 text-white"
                            : "bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expense Date <span className="text-red-500">*</span>
                </label>
                <DatePicker
                  className="w-full"
                  value={formData.expenseDate}
                  onChange={(date) =>
                    setFormData((prev) => ({ ...prev, expenseDate: date }))
                  }
                  style={{ width: "100%", height: "40px" }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantity
                </label>
                <input
                  type="text"
                  value={formData.quantity}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*\.?\d{0,2}$/.test(value) || value === "") {
                      setFormData((prev) => ({ ...prev, quantity: value }));
                    }
                  }}
                  placeholder="Quantity"
                  className="border border-gray-300 rounded px-3 py-2 w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Unit Price
                </label>
                <input
                  type="text"
                  value={formData.unitPrice}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*\.?\d{0,2}$/.test(value) || value === "") {
                      setFormData((prev) => ({ ...prev, unitPrice: value }));
                    }
                  }}
                  placeholder="Unit Price"
                  className="border border-gray-300 rounded px-3 py-2 w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.amount}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*\.?\d{0,2}$/.test(value) || value === "") {
                      if (value !== "0" && value !== "0.0" && value !== "0.00") {
                        setFormData((prev) => ({ ...prev, amount: value }));
                      } else {
                        setFormData((prev) => ({ ...prev, amount: "" }));
                      }
                    }
                  }}
                  placeholder="Amount"
                  className="border border-gray-300 rounded px-3 py-2 w-full"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setIsFormOpen(false)}
                className="px-4 py-2 bg-gray-200 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleFormSubmit}
                disabled={!isSaveButtonEnabled}
                className={`px-4 py-2 text-white rounded ${
                  isSaveButtonEnabled
                    ? "bg-primary-600 hover:bg-primary-700"
                    : "bg-gray-400 cursor-not-allowed"
                }`}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
}
